import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import SwitchInput from "../shared/Inputs/SwitchInput";
import { TextInput } from "../shared/Inputs/TextInput";
import SelectInput from "../shared/Inputs/SelectInput";

import useHiddenPageSingle from "../../data/useHiddenPageSingle";
import { IHiddenPage, WidgetPage, PaymentMethod } from "../../models/HiddenPages";
import enumToOptions from "../../utils/enumToOptions";
import safeParseJson from "../../utils/safeParseJson";
import HiddenPaymentDetails from "./pages/HiddenPaymentDetails";
import HiddenEngineCondition from "./pages/HiddenEngineCondition";
import HiddenBodyCondition from "./pages/HiddenBodyCondition";
import HiddenQuoteSelect from "./pages/HiddenQuoteSelect";


const toSpacedLabel = (s: string) =>
  s
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")   // fooBar -> foo Bar
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2") // ABCWord -> ABC Word
    .trim();

export default function HiddenPageSingle({
  hiddenPage,
  onSuccess,
}: {
  hiddenPage?: IHiddenPage;
  onSuccess: () => void;
}) {
  const { create, update, deleteSetting } = useHiddenPageSingle({ hiddenPageId: hiddenPage?.id });
  const [error, setError] = useState<string | null>(null);

  const widgetPageOptions = useMemo(() => enumToOptions(WidgetPage), []);

  const defaultValues = useMemo(() => {
    const page = hiddenPage?.page ?? WidgetPage.VehicleInfo;
    const parsed = safeParseJson(hiddenPage?.defaultValuesJson);
    const defaults =
      page === WidgetPage.PaymentDetails
        ? {
            depositEnabled: true,
            depositThreshold: "500",
            depositType: "percent",
            depositPercent: "5",
            depositFixed: "50",
            ...parsed,
          }
        : parsed;
    return {
      page,
      isHidden: hiddenPage?.isHidden ?? true,
      defaults,
    };
  }, [hiddenPage]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
    watch,
    getValues,
  } = useForm({
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);

    // loop through defaults and set each value
    Object.entries(defaultValues.defaults).forEach(([key, value]) => {
      setValue(`defaults.${key}`, value);
    });
  }, [defaultValues, reset]);

  const page = watch("page");

  const implementedWidgetPages = [
    WidgetPage.ContactDetails,
    WidgetPage.PaymentDetails,
    WidgetPage.EngineCondition,
    WidgetPage.BodyCondition,
    WidgetPage.QuoteSelect
  ];

  const showWarning = !implementedWidgetPages.includes(+page);
  
  const onSubmit = async (vals: any) => {
    setError(null);

    const defaultsObj = getValues("defaults");

    console.log("Defaults Object:", defaultsObj);

    const defaultValuesJson = JSON.stringify(defaultsObj);

    const req = {
      page: Number.parseInt(vals.page),
      isHidden: vals.isHidden,
      defaultValuesJson,
    };

    try {
      if (hiddenPage) {
        await update.mutateAsync({ id: hiddenPage.id, req }, {
          onSuccess: () => {
            onSuccess();
          },
          onError: (error: any) => {
            setError(error?.message ?? "Failed to update hidden page");
          }
        });
      } else {
        await create.mutateAsync(req, {
          onSuccess: () => {
            onSuccess();
          },
          onError: (error: any) => {
            setError(error?.message ?? "Failed to create hidden page");
          }
        });
      }
      onSuccess();
    } catch (e: any) {
      setError(e?.message ?? "Something went wrong");
    }
  };

  const handleDelete = async () => {
    if (!hiddenPage) return;

    setError(null);
    try {
      await deleteSetting.mutateAsync(hiddenPage.id, {
        onSuccess: () => {
          onSuccess();
        },
        onError: (error: any) => {
          setError(error?.message ?? "Failed to delete hidden page");
        }
      });
    } catch (e: any) {
      setError(e?.message ?? "Something went wrong");
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
      {error && (<div className="text-red-600 text-sm">{error}</div>)}

      {/* Page Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Page</label>
        <select
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          {...register("page", { required: "Page is required" })}
        >
          {widgetPageOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {toSpacedLabel(option.name)}
            </option>
          ))}
        </select>
      </div>

      {/* Is Hidden Switch */}
      <div className="flex items-center gap-2">
        <SwitchInput
          label="Is this page hidden?"
          register={register}
          registerName="isHidden"
          setValue={setValue}
          watch={watch}
        />
      </div>

      {/* Warning for non-hideable pages */}
      {showWarning && (
        <div className="bg-white p-4 rounded-md shadow-sm border-b-4 border-yellow-500 text-yellow-800">
          This page cannot be hidden right now. Please contact support if you need assistance.
        </div>
      )}

      {/* Payment Details Form Fields */}
      {page == WidgetPage.PaymentDetails  && (<HiddenPaymentDetails  register={register} setValue={setValue} watch={watch} />)}
      {page == WidgetPage.EngineCondition && (<HiddenEngineCondition register={register} setValue={setValue} watch={watch} />)}
      {page == WidgetPage.BodyCondition   && (<HiddenBodyCondition   register={register} setValue={setValue} watch={watch} />)}
      {page == WidgetPage.QuoteSelect     && (<HiddenQuoteSelect     register={register} setValue={setValue} watch={watch} />)}

      <div className="flex justify-between pt-2">
        {hiddenPage ? (
          <ButtonInput
            isSubmit={false}
            label="Delete"
            onClick={handleDelete}
            disabled={isSubmitting}
            classes="bg-red-600 hover:bg-red-700"
          />
        ) : (
          <div />
        )}

        <ButtonInput
          isSubmit
          label={hiddenPage ? "Save Changes" : "Create"}
          disabled={isSubmitting}
        />
      </div>
    </form>
  );
}


export interface HiddenPageProps {
  register: any;
  setValue: any;
  watch: any;
}